/**
 * CDC recommended childhood immunization schedule 0-24 months.
 *
 * Source: CDC Recommended Child and Adolescent Immunization Schedule (US), 2024-2025.
 * Values are the standard recommended-age windows. Actual vaccine timing depends on
 * the child's pediatrician + specific brand/formulation — always check with your
 * provider. This chart is for tracking recommendations, not for clinical prescribing.
 */

export interface VaccineDose {
  vaccine: string
  dose: number
  minMonths: number
  recommendedMonths: number
  maxMonths: number
  notes?: string
}

/**
 * Ordered by recommended age, then vaccine. Each row = one dose.
 * Some vaccines (HepB, DTaP, Hib, PCV, IPV, Rotavirus) have multiple doses.
 */
export const CDC_SCHEDULE_0_24M: VaccineDose[] = [
  { vaccine: 'HepB',       dose: 1, minMonths: 0,  recommendedMonths: 0,  maxMonths: 1,  notes: 'Birth dose within 24h' },
  { vaccine: 'HepB',       dose: 2, minMonths: 1,  recommendedMonths: 2,  maxMonths: 4 },
  { vaccine: 'RV',         dose: 1, minMonths: 2,  recommendedMonths: 2,  maxMonths: 3,  notes: 'Rotavirus — before 15 weeks' },
  { vaccine: 'DTaP',       dose: 1, minMonths: 2,  recommendedMonths: 2,  maxMonths: 4 },
  { vaccine: 'Hib',        dose: 1, minMonths: 2,  recommendedMonths: 2,  maxMonths: 4 },
  { vaccine: 'PCV13',      dose: 1, minMonths: 2,  recommendedMonths: 2,  maxMonths: 4 },
  { vaccine: 'IPV',        dose: 1, minMonths: 2,  recommendedMonths: 2,  maxMonths: 4,  notes: 'Polio' },
  { vaccine: 'RV',         dose: 2, minMonths: 4,  recommendedMonths: 4,  maxMonths: 6 },
  { vaccine: 'DTaP',       dose: 2, minMonths: 4,  recommendedMonths: 4,  maxMonths: 6 },
  { vaccine: 'Hib',        dose: 2, minMonths: 4,  recommendedMonths: 4,  maxMonths: 6 },
  { vaccine: 'PCV13',      dose: 2, minMonths: 4,  recommendedMonths: 4,  maxMonths: 6 },
  { vaccine: 'IPV',        dose: 2, minMonths: 4,  recommendedMonths: 4,  maxMonths: 6 },
  { vaccine: 'DTaP',       dose: 3, minMonths: 6,  recommendedMonths: 6,  maxMonths: 8 },
  { vaccine: 'RV',         dose: 3, minMonths: 6,  recommendedMonths: 6,  maxMonths: 8, notes: 'Only if 3-dose series (e.g. RotaTeq)' },
  { vaccine: 'HepB',       dose: 3, minMonths: 6,  recommendedMonths: 6,  maxMonths: 18 },
  { vaccine: 'Influenza',  dose: 1, minMonths: 6,  recommendedMonths: 6,  maxMonths: 8,  notes: 'Annual — check season' },
  { vaccine: 'Hib',        dose: 3, minMonths: 12, recommendedMonths: 12, maxMonths: 15, notes: 'Some brands 3-dose, some 4-dose' },
  { vaccine: 'PCV13',      dose: 3, minMonths: 12, recommendedMonths: 12, maxMonths: 15 },
  { vaccine: 'MMR',        dose: 1, minMonths: 12, recommendedMonths: 12, maxMonths: 15 },
  { vaccine: 'VAR',        dose: 1, minMonths: 12, recommendedMonths: 12, maxMonths: 15, notes: 'Varicella (chickenpox)' },
  { vaccine: 'HepA',       dose: 1, minMonths: 12, recommendedMonths: 12, maxMonths: 23 },
  { vaccine: 'HepA',       dose: 2, minMonths: 18, recommendedMonths: 18, maxMonths: 23 },
  { vaccine: 'DTaP',       dose: 4, minMonths: 15, recommendedMonths: 15, maxMonths: 18 },
]

/**
 * Compute next-due vaccines for a person given their date-of-birth.
 * Returns up to `limit` upcoming doses sorted by recommended age.
 * `alreadyGiven` = set of `"<vaccine>:<dose>"` keys already recorded via
 * medication.recorded events with payload.vaccine_name.
 */
export function nextDueVaccines(
  dob: Date,
  alreadyGiven: Set<string>,
  now: Date = new Date(),
  limit = 5,
): Array<VaccineDose & { dueDate: Date; monthsUntilDue: number; overdue: boolean }> {
  const ageMonths = monthsBetween(dob, now)
  const out: Array<VaccineDose & { dueDate: Date; monthsUntilDue: number; overdue: boolean }> = []
  for (const dose of CDC_SCHEDULE_0_24M) {
    const key = `${dose.vaccine}:${dose.dose}`
    if (alreadyGiven.has(key)) continue
    const dueDate = new Date(dob)
    dueDate.setMonth(dueDate.getMonth() + dose.recommendedMonths)
    const monthsUntilDue = dose.recommendedMonths - ageMonths
    const overdue = ageMonths > dose.maxMonths
    out.push({ ...dose, dueDate, monthsUntilDue, overdue })
  }
  // Sort: overdue first (most overdue first), then upcoming by soonest
  out.sort((a, b) => {
    if (a.overdue && !b.overdue) return -1
    if (!a.overdue && b.overdue) return 1
    return a.monthsUntilDue - b.monthsUntilDue
  })
  return out.slice(0, limit)
}

function monthsBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime()
  return ms / (1000 * 60 * 60 * 24 * 30.4375)
}
